import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { areEqual } from 'Utils/equalityChecks';

import { getJobBudgets, saveJobBudgets } from '../actions';
import type { CostCategory, SaveJobBudgetsPayload } from '../types';
import { COST_CATEGORIES, COST_CATEGORY_LABELS } from '../types';
import { formatCurrency, formatSignedCurrency } from '../format';
import classes from '../jobCosting.module.css';

// Variance cell colour without nesting ternaries (eslint no-nested-ternary).
const varianceClass = (variance: number | null): string => {
  if (variance === null) return '';
  return variance >= 0 ? classes.profitPositive : classes.profitNegative;
};

interface Props {
  ssoToken: string;
  opportunityId: string;
  // Per-category actual costs from the job summary (read-only side of the comparison).
  costsByCategory: Partial<Record<CostCategory, number>>;
  // Called after a successful save so the parent can refresh the summary (budget/variance).
  onSaved: () => void;
}

const extractApiError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (first?.[0]) return first[0];
    }
    if (data?.message) return data.message;
  }
  return fallback;
};

/**
 * Budget-vs-actual editor for one job (MONGOOSE-FR-007, Phase 1 manual). One editable
 * budget input per cost category, shown beside the actual and a live variance. Empty
 * input clears that category's budget on save.
 */
function JobBudgetEditor({ ssoToken, opportunityId, costsByCategory, onSaved }: Props) {
  // Per-category budget input, as strings (empty = no budget for that category).
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getJobBudgets(ssoToken, opportunityId)
      .then((budgets) => {
        if (!mounted) return;
        const next: Record<string, string> = {};
        budgets.forEach((b) => {
          next[b.costCategory] = String(b.budgetedAmount);
        });
        setInputs(next);
      })
      .catch(() => {
        /* no budgets yet is the normal first-run state */
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [ssoToken, opportunityId]);

  const setInput = useCallback((category: CostCategory, value: string) => {
    setSavedAt(null);
    setInputs((prev) => ({ ...prev, [category]: value }));
  }, []);

  const totals = useMemo(() => {
    let budget = 0;
    let actual = 0;
    let anyBudget = false;
    COST_CATEGORIES.forEach((c) => {
      const raw = inputs[c];
      const actualC = Number(costsByCategory[c] ?? 0);
      actual += actualC;
      if (raw !== undefined && raw !== '') {
        budget += Number(raw) || 0;
        anyBudget = true;
      }
    });
    return { budget, actual, variance: budget - actual, anyBudget };
  }, [inputs, costsByCategory]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Full category map: empty input clears that category (backend deletes the row).
      const payload: SaveJobBudgetsPayload = {};
      COST_CATEGORIES.forEach((c) => {
        const raw = inputs[c];
        payload[c] = raw === undefined || raw === '' ? null : Number(raw);
      });
      const saved = await saveJobBudgets(ssoToken, opportunityId, payload);
      // Re-seed inputs from the server's authoritative values (e.g. rounding), so the
      // editor reflects exactly what was persisted rather than the raw typed strings.
      const next: Record<string, string> = {};
      saved.forEach((b) => {
        next[b.costCategory] = String(b.budgetedAmount);
      });
      setInputs(next);
      setSavedAt(Date.now());
      onSaved();
    } catch (err) {
      setError(extractApiError(err, 'Failed to save budgets.'));
    } finally {
      setSaving(false);
    }
  }, [ssoToken, opportunityId, inputs, onSaved]);

  if (loading) {
    return (
      <div className={classes.loadingState}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>Category</th>
              <th className={classes.num}>Budget</th>
              <th className={classes.num}>Actual</th>
              <th className={classes.num}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {COST_CATEGORIES.map((c) => {
              const raw = inputs[c];
              const hasBudget = raw !== undefined && raw !== '';
              const actual = Number(costsByCategory[c] ?? 0);
              const variance = hasBudget ? (Number(raw) || 0) - actual : null;
              return (
                <tr key={c}>
                  <td>{COST_CATEGORY_LABELS[c]}</td>
                  <td className={classes.num}>
                    <input
                      className={classes.input}
                      type="number"
                      min="0"
                      step="0.01"
                      aria-label={`${COST_CATEGORY_LABELS[c]} budget`}
                      value={raw ?? ''}
                      placeholder="—"
                      onChange={(e) => setInput(c, e.target.value)}
                    />
                  </td>
                  <td className={classes.num}>{formatCurrency(actual)}</td>
                  <td className={`${classes.num} ${varianceClass(variance)}`}>
                    {variance === null ? '—' : formatSignedCurrency(variance)}
                  </td>
                </tr>
              );
            })}
            <tr className={classes.totalsRow}>
              <td>Total</td>
              <td className={classes.num}>{totals.anyBudget ? formatCurrency(totals.budget) : '—'}</td>
              <td className={classes.num}>{formatCurrency(totals.actual)}</td>
              <td className={`${classes.num} ${varianceClass(totals.anyBudget ? totals.variance : null)}`}>
                {totals.anyBudget ? formatSignedCurrency(totals.variance) : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {error && <div className={classes.fieldError}>{error}</div>}

      <div className={classes.saveBudgetRow}>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save budget'}
        </button>
        {savedAt !== null && !saving && <span className="ms-2 text-success">Saved.</span>}
      </div>
    </div>
  );
}

const JobBudgetEditorMemo = memo(JobBudgetEditor, areEqual);

export { JobBudgetEditorMemo as JobBudgetEditor };
